/**
 * CHAT-01 (Seth 2026-08-26): private team messaging inside Pegasus.
 * A chat bubble lives beside the Ask-Pegasus button on every page: pick a
 * teammate, type, send. History keeps forever (pegasus_dm, RLS = only the two
 * of you). Delivery is LIVE — Supabase realtime streams new rows to the
 * recipient's open tab: unread badge on the bubble, a click-to-open toast,
 * a "(2) Pegasus" tab title, and a system notification when the tab is
 * hidden. Not Slack — just "I need Holly to see this NOW" without a phone.
 */
import { useEffect, useMemo, useRef, useState, type ReactNode } from "react";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { MessageCircle, Send, ArrowLeft, X, Search, Smile, Paperclip, AtSign, User, Hammer, Loader2, Download } from "lucide-react";
import { Link } from "react-router-dom";
import { supabase } from "@/lib/supabase";
import { cn } from "@/lib/cn";
import { TechAvatar } from "@/components/TechAvatar";
import { useChatPeople } from "@/features/mobile/field";
import { useCan } from "@/lib/permissions/PermissionProvider";
import { useCustomerSearch } from "@/features/customer-360/api";
import { useDispatchSearch } from "@/features/universal-search/useDispatchSearch";
import { Wrench, Receipt, ShoppingCart } from "lucide-react";
import { setChatOpen, useDockState } from "./dockState";

// The Pegasus (System) contact now rides in from the shared roster hook
// (SYSTEM_CHAT in features/mobile/field.tsx) — one definition, both ends.

/** CHAT-02: the emoji library — curated for shop talk, grouped by use. */
const EMOJI_GROUPS: { label: string; emoji: string[] }[] = [
  { label: "Faces", emoji: ["😀","😄","😁","😆","😅","😂","🤣","🙂","😊","😉","😍","🥰","😎","🤩","🥳","😏","😬","🙄","😴","🤯","😳","🥺","😢","😭","😤","😠","🤔","🤫","🤗","🫡","😷","🤒","🥶","🥵","🤠","🫠"] },
  { label: "Hands", emoji: ["👍","👎","👊","✊","👏","🙌","🤝","🙏","💪","👌","🤌","✌️","🤞","🤟","🖐️","✋","👋","🫶","👉","👈","👆","👇","🫵","🤘"] },
  { label: "Work", emoji: ["🔧","🔨","🛠️","⚙️","🔩","🪛","🧰","📦","🚚","🚐","🏠","🏢","🔥","❄️","🌡️","💨","💧","🧊","⚡","🔌","🚿","🛁","🪜","🧯","🕳️","🚪","🪟","🧹","🧲","🔦"] },
  { label: "Status", emoji: ["✅","❌","⚠️","❗","❓","💯","🎯","🕐","⏰","⏳","📅","📋","📝","📎","📌","🔒","🔓","🔔","🔕","🚨","🟢","🟡","🔴","🚫","♻️","🆗","🆕","🔁"] },
  { label: "Money", emoji: ["💰","💵","💳","🧾","📈","📉","🏦","🤑","💸","🪙"] },
  { label: "Fun", emoji: ["🎉","🎊","🥇","🏆","⭐","🌟","✨","🎁","🍕","🌮","🍩","☕","🥤","🍺","🎂","⚽","🏈","🎣","🐶","🐱","🦅","🌞","🌧️","⛈️","🌈","💤","💥","💡","🚀","🛞"] },
];

interface Dm {
  id: string; from_email: string; to_email: string; body: string;
  created_at: string; read_at: string | null;
  attachment_path: string | null; attachment_name: string | null; attachment_type: string | null;
}
interface Person { email: string; empno: string | null; name: string; role: string | null }

const low = (s: string | null | undefined) => (s ?? "").trim().toLowerCase();

function useMe() {
  return useQuery({
    queryKey: ["chat_me"],
    staleTime: Infinity,
    queryFn: async () => low((await supabase.auth.getUser()).data.user?.email),
  }).data ?? "";
}

/** The whole current team — same roster brain as Pegasus Field (tech_profile
 *  staff directory + mirror names + app_user logins), so both ends of chat
 *  always show the same people, each with their job title. */
function useDirectory(me: string) {
  const q = useChatPeople(me);
  return useMemo<Person[]>(
    () => (q.data ?? [])
      // Desktop keeps it to rows you can actually open — incl. yourself:
      // your own thread is "Notes to self" (matches the phone).
      .filter((p) => p.email)
      .map((p) => p.email === me
        ? { email: p.email, empno: p.empno, name: `${p.name} (you)`, role: "Notes to self — only you see these" }
        : { email: p.email, empno: p.empno, name: p.name, role: p.role ?? p.group }),
    [q.data, me],
  );
}

/** Every message involving me — one query powers the list, unread counts, and threads. */
function useAllDms(me: string) {
  return useQuery({
    queryKey: ["dm", me],
    enabled: !!me,
    refetchInterval: 30 * 1000, // safety net under the realtime stream
    queryFn: async (): Promise<Dm[]> => {
      const { data } = await supabase.from("pegasus_dm").select("*")
        .order("created_at", { ascending: true }).limit(2000);
      return (data ?? []) as Dm[];
    },
  });
}

// CHAT-03: record tags — customers, jobs, dispatches (calls), invoices —
// render as chips that navigate straight to the record.
const TAG_RE = /\[\[(cust|job|disp|inv|po|epo):([^|\]]+)\|([^\]]+)\]\]/g;
const TAG_ROUTE: Record<string, (id: string) => string> = {
  cust: (id) => `/customers/${id}`,
  job: (id) => `/jobs/${id}`,
  disp: (id) => `/dispatch/${id}`,
  inv: (id) => `/invoice/${id}`,
  po: (id) => `/purchase-orders?po=${id}`,
  epo: (id) => `/purchase-orders?escpo=${id}`,
};
const TAG_ICON: Record<string, typeof User> = { cust: User, job: Hammer, disp: Wrench, inv: Receipt, po: ShoppingCart, epo: ShoppingCart };
const TAG_TITLE: Record<string, string> = {
  cust: "Open this customer's 360", job: "Open this job",
  disp: "Open this service call", inv: "Open this invoice",
  po: "Open this purchase order", epo: "Open this purchase order",
};

function BodyText({ body, mine }: { body: string; mine: boolean }) {
  const parts: ReactNode[] = [];
  let last = 0;
  let m: RegExpExecArray | null;
  const re = new RegExp(TAG_RE.source, "g");
  while ((m = re.exec(body))) {
    if (m.index > last) parts.push(body.slice(last, m.index));
    const [, kind, id, label] = m;
    const Icon = TAG_ICON[kind] ?? User;
    parts.push(
      <Link key={`${m.index}`} to={(TAG_ROUTE[kind] ?? TAG_ROUTE.cust)(id)}
        title={TAG_TITLE[kind]}
        className={cn(
          "mx-0.5 inline-flex items-center gap-1 rounded-md px-1.5 py-0.5 align-middle text-[13px] font-bold underline-offset-2 hover:underline",
          mine ? "bg-white/20 text-white" : "bg-brand-50 text-brand-700",
        )}>
        <Icon className="h-3 w-3" />
        {label}
      </Link>,
    );
    last = m.index + m[0].length;
  }
  if (last < body.length) parts.push(body.slice(last));
  return <>{parts}</>;
}

/** Strip tag markup for previews/toasts: "[[job:..|Furnace]]" → "Furnace". */
const plainBody = (d: Pick<Dm, "body" | "attachment_name">) => {
  const t = d.body.replace(TAG_RE, "$3").trim();
  return t || (d.attachment_name ? `📎 ${d.attachment_name}` : "");
};

/** ESC "Last,First 0012345" → "First Last". */
const cleanEscName = (raw: string | null | undefined, fallback: string) => {
  let n = (raw ?? fallback).replace(/\s*\d{5,}\s*$/, "").trim();
  if (n.includes(",")) { const i = n.indexOf(","); n = `${n.slice(i + 1).trim()} ${n.slice(0, i).trim()}`; }
  return n || fallback;
};

type TagKind = "cust" | "job" | "disp" | "inv" | "po" | "epo";

/** CHAT-03b (Seth 2026-08-26 "just a universal tagging search"): ONE box,
 *  every record the team talks about — customer names/addresses/phones,
 *  dispatch #s, invoice #s, PO #s, jobs — grouped results, no tabs.
 *  Customers + calls ride the SAME hooks as the header search / Ctrl+K. */
function TagPicker({ onPick, onClose }: {
  onPick: (kind: TagKind, id: string, label: string) => void;
  onClose: () => void;
}) {
  const [t, setT] = useState("");
  const s = t.trim();
  const numeric = /\d/.test(s);
  const customers = useCustomerSearch(s.length >= 2 ? s : "");
  const dispatches = useDispatchSearch(s.length >= 3 && numeric ? s : "");
  const pat = `%${s.replace(/[%_]/g, (m) => "\\" + m)}%`;
  const invoices = useQuery({
    queryKey: ["chat_tag_inv", s],
    enabled: s.length >= 3 && numeric,
    queryFn: async (): Promise<{ invoice: string; inv_amt: number | null; inv_date: string | null; custno: string }[]> => {
      const { data } = await supabase.from("mirror_receivable")
        .select("invoice, inv_amt, inv_date, custno")
        .ilike("invoice", pat).order("inv_date", { ascending: false }).limit(4);
      return (data ?? []) as { invoice: string; inv_amt: number | null; inv_date: string | null; custno: string }[];
    },
  });
  const pos = useQuery({
    queryKey: ["chat_tag_po", s],
    enabled: s.length >= 2,
    queryFn: async (): Promise<{ kind: "po" | "epo"; id: string; num: string; vendor: string | null }[]> => {
      const [{ data: nat }, { data: esc }] = await Promise.all([
        supabase.from("pegasus_po").select("id, po_number, vendor_code")
          .or(`po_number.ilike.${pat},vendor_code.ilike.${pat}`).limit(4),
        supabase.from("mirror_po").select("po, vendor_code")
          .or(`po.ilike.${pat},vendor_code.ilike.${pat}`).order("date", { ascending: false, nullsFirst: false }).limit(4),
      ]);
      return [
        ...((nat ?? []) as { id: string; po_number: string | null; vendor_code: string | null }[])
          .map((p) => ({ kind: "po" as const, id: p.id, num: p.po_number ?? "—", vendor: p.vendor_code })),
        ...((esc ?? []) as { po: string; vendor_code: string | null }[])
          .map((p) => ({ kind: "epo" as const, id: p.po, num: p.po, vendor: p.vendor_code })),
      ].slice(0, 4);
    },
  });
  const jobs = useQuery({
    queryKey: ["chat_tag_job", s.toLowerCase()],
    enabled: s.length >= 2,
    queryFn: async (): Promise<{ id: string; title: string | null; address: string | null }[]> => {
      const { data } = await supabase.from("pegasus_job")
        .select("id, title, address")
        .or(`title.ilike.${pat},address.ilike.${pat}`)
        .order("created_at", { ascending: false }).limit(4);
      return (data ?? []) as { id: string; title: string | null; address: string | null }[];
    },
  });

  const busy = customers.isFetching || dispatches.isFetching || invoices.isFetching || pos.isFetching || jobs.isFetching;
  const custRows = (customers.data ?? []).slice(0, 4);
  const dispRows = dispatches.data ?? [];
  const invRows = invoices.data ?? [];
  const poRows = pos.data ?? [];
  const jobRows = jobs.data ?? [];
  const nothing = s.length >= 2 && !busy &&
    custRows.length + dispRows.length + invRows.length + poRows.length + jobRows.length === 0;

  const rowCls = "flex w-full items-center gap-2 px-3 py-2 text-left text-[13px] transition-colors hover:bg-slate-50";
  const GroupHead = ({ label }: { label: string }) => (
    <div className="px-3 pb-0.5 pt-2 text-[10px] font-bold uppercase tracking-wider text-slate-400">{label}</div>
  );
  const fmtDate = (iso: string | null) => (iso ? new Date(`${String(iso).slice(0, 10)}T12:00:00`).toLocaleDateString("en-US", { month: "short", day: "numeric", year: "2-digit" }) : "");

  return (
    <div className="absolute bottom-full left-2 right-2 z-10 mb-1 overflow-hidden rounded-xl border border-hairline bg-white shadow-xl">
      <div className="flex items-center gap-2 border-b border-slate-100 p-2">
        <input autoFocus value={t} onChange={(e) => setT(e.target.value)}
          placeholder="Name, address, call #, invoice #, PO #, job…"
          className="min-w-0 flex-1 rounded-lg border border-hairline px-2.5 py-1.5 text-sm focus:border-brand-400 focus:outline-none" />
        {busy && <Loader2 className="h-4 w-4 shrink-0 animate-spin text-slate-300" />}
        <button type="button" onClick={onClose} className="shrink-0 rounded p-1 text-slate-300 hover:text-slate-500"><X className="h-3.5 w-3.5" /></button>
      </div>
      <div className="max-h-64 overflow-y-auto pb-1">
        {custRows.length > 0 && <GroupHead label="Customers" />}
        {custRows.map((c) => {
          const name = cleanEscName(c.fullname, c.custno);
          const loc = c.locations?.[0];
          return (
            <button key={`c${c.custno}`} type="button" className={rowCls} onClick={() => onPick("cust", c.custno, name)}>
              <User className="h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span className="min-w-0 truncate font-semibold text-slate-700">{name}</span>
              <span className="ml-auto min-w-0 shrink truncate text-[11px] text-slate-400">{loc?.add1 ?? c.custno}</span>
            </button>
          );
        })}
        {dispRows.length > 0 && <GroupHead label="Service Calls" />}
        {dispRows.map((d) => {
          const name = cleanEscName(d.customer_fullname, d.custno);
          return (
            <button key={`d${d.dispatch}`} type="button" className={rowCls}
              onClick={() => onPick("disp", d.dispatch, `Call #${d.dispatch} — ${name}`)}>
              <Wrench className="h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span className="min-w-0 truncate font-semibold text-slate-700">#{d.dispatch} · {name}</span>
              <span className="ml-auto shrink-0 text-[11px] text-slate-400">{fmtDate(d.appointment_date)}</span>
            </button>
          );
        })}
        {invRows.length > 0 && <GroupHead label="Invoices" />}
        {invRows.map((i) => (
          <button key={`i${i.invoice}`} type="button" className={rowCls}
            onClick={() => onPick("inv", i.invoice, `Invoice #${i.invoice}`)}>
            <Receipt className="h-3.5 w-3.5 shrink-0 text-brand-600" />
            <span className="min-w-0 truncate font-semibold text-slate-700">#{i.invoice}</span>
            <span className="ml-auto shrink-0 text-[11px] tabular-nums text-slate-400">
              {i.inv_amt != null ? Number(i.inv_amt).toLocaleString("en-US", { style: "currency", currency: "USD", maximumFractionDigits: 0 }) : ""}{i.inv_date ? ` · ${fmtDate(i.inv_date)}` : ""}
            </span>
          </button>
        ))}
        {poRows.length > 0 && <GroupHead label="Purchase Orders" />}
        {poRows.map((p) => (
          <button key={`p${p.kind}${p.id}`} type="button" className={rowCls}
            onClick={() => onPick(p.kind, p.id, `PO #${p.num}`)}>
            <ShoppingCart className="h-3.5 w-3.5 shrink-0 text-brand-600" />
            <span className="min-w-0 truncate font-semibold text-slate-700">PO #{p.num}</span>
            <span className="ml-auto shrink-0 text-[11px] text-slate-400">{p.vendor ?? ""}</span>
          </button>
        ))}
        {jobRows.length > 0 && <GroupHead label="Jobs" />}
        {jobRows.map((j) => {
          const label = [j.title, j.address].filter(Boolean).join(" — ").slice(0, 60) || "Job";
          return (
            <button key={`j${j.id}`} type="button" className={rowCls} onClick={() => onPick("job", j.id, label)}>
              <Hammer className="h-3.5 w-3.5 shrink-0 text-brand-600" />
              <span className="min-w-0 truncate font-semibold text-slate-700">{j.title ?? "Job"}</span>
              <span className="ml-auto min-w-0 shrink truncate text-[11px] text-slate-400">{j.address ?? ""}</span>
            </button>
          );
        })}
        {s.length < 2 && (
          <p className="px-3 py-4 text-center text-xs text-slate-400">
            Type anything — a name, an address, a call #, an invoice #, a PO #…
          </p>
        )}
        {nothing && <p className="px-3 py-4 text-center text-xs text-slate-400">Nothing matches “{s}”.</p>}
      </div>
    </div>
  );
}

/** One message's attachment — image inline, anything else a download chip. */
function Attachment({ d, mine }: { d: Dm; mine: boolean }) {
  const { data: url } = useQuery({
    queryKey: ["chat_att", d.attachment_path],
    enabled: !!d.attachment_path,
    staleTime: 45 * 60 * 1000,
    queryFn: async () => {
      const { data } = await supabase.storage.from("chat-files").createSignedUrl(d.attachment_path!, 60 * 60);
      return data?.signedUrl ?? null;
    },
  });
  if (!d.attachment_path) return null;
  const isImage = (d.attachment_type ?? "").startsWith("image/");
  if (isImage && url) {
    return (
      <a href={url} target="_blank" rel="noreferrer" title={d.attachment_name ?? "image"}>
        <img src={url} alt={d.attachment_name ?? "attachment"} className="mt-1 max-h-48 max-w-full rounded-xl object-cover" />
      </a>
    );
  }
  return (
    <a href={url ?? "#"} target="_blank" rel="noreferrer"
      className={cn(
        "mt-1 flex items-center gap-2 rounded-xl px-3 py-2 text-[13px] font-semibold",
        mine ? "bg-white/15 text-white hover:bg-white/25" : "bg-slate-100 text-slate-700 hover:bg-slate-200",
      )}>
      <Paperclip className="h-4 w-4 shrink-0" />
      <span className="min-w-0 truncate">{d.attachment_name ?? "Attachment"}</span>
      <Download className="ml-auto h-3.5 w-3.5 shrink-0 opacity-60" />
    </a>
  );
}

const timeLabel = (iso: string) => {
  const d = new Date(iso);
  const now = new Date();
  const sameDay = d.toDateString() === now.toDateString();
  return sameDay
    ? d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" })
    : d.toLocaleDateString("en-US", { month: "short", day: "numeric" });
};

export function ChatDock() {
  const me = useMe();
  // CHAT-PERM-01: team chat is a grantable capability — on for every role,
  // revocable per user ("Never" in Settings → Users & Roles).
  const canChat = useCan("chat.use");
  const qc = useQueryClient();
  const people = useDirectory(me);
  const { data: dms } = useAllDms(me);
  const [open, setOpen] = useState(false);
  const [who, setWho] = useState<string | null>(null); // email of the open thread
  const [term, setTerm] = useState("");
  const [draft, setDraft] = useState("");
  // CHAT-04 (Seth 2026-08-31: "it's a bunch of random code in the text box"):
  // picked tags live as CHIPS above the composer, never as [[markup]] in the
  // text. The markup is assembled invisibly at send time.
  const [pendingTags, setPendingTags] = useState<{ kind: TagKind; id: string; label: string }[]>([]);
  const [emojiOpen, setEmojiOpen] = useState(false);
  const [emojiTab, setEmojiTab] = useState(0);
  const [tagOpen, setTagOpen] = useState(false);
  const [fromAt, setFromAt] = useState(false); // picker opened by typing "@"
  const [toast, setToast] = useState<{ from: string; body: string } | null>(null);
  const endRef = useRef<HTMLDivElement>(null);
  const openRef = useRef({ open, who });
  openRef.current = { open, who };
  // DOCK-01: share the corner with Ask Pegasus — never stack on its window.
  const { aiOpen } = useDockState();
  useEffect(() => { setChatOpen(open); return () => setChatOpen(false); }, [open]);

  const all = dms ?? [];
  const unreadBy = useMemo(() => {
    const m = new Map<string, number>();
    for (const d of all) if (low(d.to_email) === me && !d.read_at) m.set(low(d.from_email), (m.get(low(d.from_email)) ?? 0) + 1);
    return m;
  }, [all, me]);
  const unreadTotal = [...unreadBy.values()].reduce((s, n) => s + n, 0);

  // "(2) " tab-title prefix — the away-from-the-tab nudge.
  useEffect(() => {
    const base = document.title.replace(/^\(\d+\)\s*/, "");
    document.title = canChat && unreadTotal > 0 ? `(${unreadTotal}) ${base}` : base;
  }, [unreadTotal, canChat]);

  // LIVE: new messages to me stream in — refresh, toast, system notification.
  useEffect(() => {
    if (!me || !canChat) return;
    const ch = supabase
      .channel("pegasus_dm_live")
      .on("postgres_changes", { event: "INSERT", schema: "public", table: "pegasus_dm", filter: `to_email=eq.${me}` }, (payload) => {
        void qc.invalidateQueries({ queryKey: ["dm", me] });
        const row = payload.new as Dm;
        const cur = openRef.current;
        const viewing = cur.open && low(cur.who) === low(row.from_email);
        if (!viewing) setToast({ from: low(row.from_email), body: plainBody(row) });
        if (document.hidden && "Notification" in window && Notification.permission === "granted") {
          try { new Notification("Pegasus message", { body: row.body.slice(0, 120) }); } catch { /* blocked */ }
        }
      })
      .subscribe();
    return () => { void supabase.removeChannel(ch); };
  }, [me, canChat, qc]);

  const send = useMutation({
    mutationFn: async (v: { to: string; body: string; attachment?: { path: string; name: string; type: string } }) => {
      const { error } = await supabase.from("pegasus_dm").insert({
        from_email: me, to_email: v.to, body: v.body,
        attachment_path: v.attachment?.path ?? null,
        attachment_name: v.attachment?.name ?? null,
        attachment_type: v.attachment?.type ?? null,
      });
      if (error) throw error;
    },
    onSuccess: () => { setDraft(""); void qc.invalidateQueries({ queryKey: ["dm", me] }); },
  });

  // CHAT-03: attach a file — uploads to the private chat bucket, sends as its
  // own message (with whatever text is in the draft riding along).
  const fileRef = useRef<HTMLInputElement>(null);
  const [uploading, setUploading] = useState(false);
  const attachFile = async (list: FileList | null) => {
    const file = list?.[0];
    if (!file || !who) return;
    if (file.size > 25 * 1024 * 1024) { alert("Files up to 25 MB."); return; }
    setUploading(true);
    try {
      const path = `${me.replace(/[^a-z0-9]/gi, "_")}/${Date.now()}_${file.name.replace(/[^\w.\- ]/g, "_")}`;
      const { error } = await supabase.storage.from("chat-files").upload(path, file, { contentType: file.type || "application/octet-stream" });
      if (error) throw error;
      send.mutate({ to: who, body: draft.trim(), attachment: { path, name: file.name, type: file.type || "application/octet-stream" } });
    } catch (e) {
      alert((e as Error).message || "Upload failed.");
    } finally {
      setUploading(false);
      if (fileRef.current) fileRef.current.value = "";
    }
  };

  const markRead = useMutation({
    mutationFn: async (from: string) => {
      await supabase.from("pegasus_dm").update({ read_at: new Date().toISOString() })
        .eq("to_email", me).eq("from_email", from).is("read_at", null);
    },
    onSuccess: () => void qc.invalidateQueries({ queryKey: ["dm", me] }),
  });

  const openThread = (email: string) => {
    setWho(email);
    setToast(null);
    if ((unreadBy.get(email) ?? 0) > 0) markRead.mutate(email);
    if ("Notification" in window && Notification.permission === "default") void Notification.requestPermission();
  };

  // ROSTER-01: anywhere in Pegasus can open a chat with someone —
  // window.dispatchEvent(new CustomEvent("pegasus:open-chat", { detail: { email } }))
  useEffect(() => {
    const onOpen = (e: Event) => {
      const email = low((e as CustomEvent<{ email?: string }>).detail?.email);
      if (!email) return;
      setOpen(true);
      setToast(null);
      setWho(email);
      if ("Notification" in window && Notification.permission === "default") void Notification.requestPermission();
    };
    window.addEventListener("pegasus:open-chat", onOpen);
    return () => window.removeEventListener("pegasus:open-chat", onOpen);
  }, []);

  const thread = useMemo(
    () => (who ? all.filter((d) => (low(d.from_email) === me && low(d.to_email) === who) || (low(d.from_email) === who && low(d.to_email) === me)) : []),
    [all, who, me],
  );
  useEffect(() => { endRef.current?.scrollIntoView({ block: "end" }); }, [thread.length, open, who]);
  // New messages arriving while the thread is open get read-stamped too.
  useEffect(() => {
    if (open && who && (unreadBy.get(who) ?? 0) > 0) markRead.mutate(who);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [open, who, unreadBy]);

  const lastByPerson = useMemo(() => {
    const m = new Map<string, Dm>();
    for (const d of all) {
      const other = low(d.from_email) === me ? low(d.to_email) : low(d.from_email);
      m.set(other, d); // list is time-ascending, so the last write wins
    }
    return m;
  }, [all, me]);

  const roster = useMemo(() => {
    const t = term.trim().toLowerCase();
    // Job titles count in search too — "CSR" finds the CSR by role alone.
    const rows = people.filter((p) => !t || p.name.toLowerCase().includes(t) || p.email.includes(t) || (p.role ?? "").toLowerCase().includes(t));
    return rows.sort((a, b) => {
      const ua = unreadBy.get(a.email) ?? 0, ub = unreadBy.get(b.email) ?? 0;
      if (ua !== ub) return ub - ua;
      const la = lastByPerson.get(a.email)?.created_at ?? "", lb = lastByPerson.get(b.email)?.created_at ?? "";
      if (la !== lb) return lb.localeCompare(la);
      return a.name.localeCompare(b.name);
    });
  }, [people, term, unreadBy, lastByPerson]);

  const whoPerson = who ? people.find((p) => p.email === who) ?? { email: who, empno: null, name: who.split("@")[0], role: null } : null;
  const doSend = () => {
    const tagText = pendingTags.map((t) => `[[${t.kind}:${t.id}|${t.label}]]`).join(" ");
    const body = [draft.trim(), tagText].filter(Boolean).join(" ").trim();
    if (!body || !who || send.isPending) return;
    setEmojiOpen(false);
    setTagOpen(false);
    setDraft("");
    setPendingTags([]);
    send.mutate({ to: who, body });
  };

  if (!me || !canChat) return null; // no login, or chat revoked for this user

  return (
    <>
      {/* The bubble — lives to the LEFT of Ask Pegasus, and steps aside
          entirely while the AI window is open (DOCK-01). */}
      {!open && !aiOpen && (
        <button
          type="button"
          data-stay-open
          onClick={() => { setOpen(true); setToast(null); }}
          title="Team chat — private messages"
          className={cn(
            "fixed bottom-5 right-[4.75rem] z-40 flex h-12 w-12 items-center justify-center rounded-full [pointer-events:auto]",
            "bg-white text-brand-700 shadow-lg ring-1 ring-hairline",
            "transition-transform duration-200 hover:scale-105 motion-reduce:hover:scale-100",
          )}
        >
          <MessageCircle className="h-5 w-5" />
          {unreadTotal > 0 && (
            <span className="absolute -right-1 -top-1 flex h-5 min-w-[1.25rem] items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white">
              {unreadTotal > 9 ? "9+" : unreadTotal}
            </span>
          )}
        </button>
      )}

      {/* Incoming-message toast (click = open that thread). */}
      {toast && !open && (
        <button
          type="button"
          onClick={() => { setOpen(true); openThread(toast.from); }}
          className={cn(
            "fixed z-50 flex w-72 items-start gap-2.5 rounded-xl border border-hairline bg-white p-3 text-left shadow-2xl",
            aiOpen ? "right-5 top-16" : "bottom-20 right-5", // above the header when the AI window owns the corner
          )}
        >
          <MessageCircle className="mt-0.5 h-4 w-4 shrink-0 text-brand-600" />
          <span className="min-w-0">
            <span className="block text-[13px] font-bold text-slate-800">
              {people.find((p) => p.email === toast.from)?.name ?? toast.from.split("@")[0]}
            </span>
            <span className="block truncate text-[13px] text-slate-500">{toast.body}</span>
          </span>
          <X className="ml-auto h-3.5 w-3.5 shrink-0 text-slate-300 hover:text-slate-500"
            onClick={(e) => { e.stopPropagation(); setToast(null); }} />
        </button>
      )}

      {open && (
        <div data-stay-open className="fixed bottom-5 right-5 z-50 flex h-[640px] [pointer-events:auto] max-h-[82vh] w-[440px] max-w-[calc(100vw-2.5rem)] flex-col overflow-hidden rounded-2xl border border-hairline bg-white shadow-2xl">
          {/* Header */}
          <div className="flex items-center gap-2 border-b border-slate-100 bg-brand-700 px-4 py-3 text-white">
            {who ? (
              <>
                <button type="button" onClick={() => { setWho(null); setEmojiOpen(false); setTagOpen(false); }} className="rounded p-1 transition-colors hover:bg-white/15" title="All conversations">
                  <ArrowLeft className="h-4 w-4" />
                </button>
                <TechAvatar empno={whoPerson?.empno ?? ""} name={whoPerson?.name ?? ""} size="sm" />
                <h3 className="min-w-0 truncate text-sm font-bold">
                  {whoPerson?.name}
                  {whoPerson?.role && <span className="ml-1.5 text-[11px] font-normal text-white/60">{whoPerson.role}</span>}
                </h3>
              </>
            ) : (
              <>
                <MessageCircle className="h-4 w-4" />
                <h3 className="text-sm font-bold">Team Chat</h3>
              </>
            )}
            <button type="button" onClick={() => setOpen(false)} className="ml-auto rounded p-1 transition-colors hover:bg-white/15" title="Close">
              <X className="h-4 w-4" />
            </button>
          </div>

          {!who ? (
            <>
              {/* People list */}
              <div className="border-b border-slate-100 p-2.5">
                <div className="relative">
                  <Search className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
                  <input value={term} onChange={(e) => setTerm(e.target.value)} placeholder="Search the team…"
                    className="w-full rounded-lg border border-hairline py-2 pl-8 pr-3 text-sm focus:border-brand-400 focus:outline-none" />
                </div>
              </div>
              <div className="flex-1 overflow-y-auto py-1">
                {roster.map((p) => {
                  const unread = unreadBy.get(p.email) ?? 0;
                  const last = lastByPerson.get(p.email);
                  return (
                    <button key={p.email} type="button" onClick={() => openThread(p.email)}
                      className="flex w-full items-center gap-3 px-3.5 py-2.5 text-left transition-colors hover:bg-slate-50">
                      <TechAvatar empno={p.empno ?? ""} name={p.name} size="md" />
                      <span className="min-w-0 flex-1">
                        <span className="flex items-baseline justify-between gap-2">
                          <span className={cn("truncate text-sm", unread > 0 ? "font-bold text-slate-900" : "font-semibold text-slate-700")}>{p.name}</span>
                          {last && <span className="shrink-0 text-[11px] text-slate-400">{timeLabel(last.created_at)}</span>}
                        </span>
                        <span className={cn("block truncate text-[13px]", unread > 0 ? "font-medium text-slate-700" : "text-slate-400")}>
                          {last ? `${low(last.from_email) === me ? "You: " : ""}${plainBody(last)}` : (p.role ?? "Start a conversation")}
                        </span>
                      </span>
                      {unread > 0 && (
                        <span className="flex h-5 min-w-[1.25rem] shrink-0 items-center justify-center rounded-full bg-rose-600 px-1 text-[11px] font-bold text-white">{unread}</span>
                      )}
                    </button>
                  );
                })}
                {roster.length === 0 && <p className="py-10 text-center text-sm text-slate-400">Nobody matches that search.</p>}
              </div>
            </>
          ) : (
            <>
              {/* Thread */}
              <div className="flex-1 space-y-1.5 overflow-y-auto bg-slate-50/60 px-3.5 py-3">
                {thread.map((d, i) => {
                  const mine = low(d.from_email) === me;
                  const prev = thread[i - 1];
                  const gap = !prev || new Date(d.created_at).getTime() - new Date(prev.created_at).getTime() > 30 * 60e3;
                  return (
                    <div key={d.id}>
                      {gap && <div className="py-1.5 text-center text-[11px] font-medium text-slate-400">{timeLabel(d.created_at)}</div>}
                      <div className={cn("flex", mine ? "justify-end" : "justify-start")}>
                        <span className={cn(
                          "max-w-[80%] whitespace-pre-wrap break-words rounded-2xl px-3.5 py-2 text-sm leading-relaxed",
                          mine ? "rounded-br-md bg-brand-700 text-white" : "rounded-bl-md bg-white text-slate-800 shadow-sm ring-1 ring-hairline",
                        )}>
                          {d.body.trim() !== "" && <BodyText body={d.body} mine={mine} />}
                          <Attachment d={d} mine={mine} />
                        </span>
                      </div>
                    </div>
                  );
                })}
                {thread.length === 0 && (
                  <p className="py-10 text-center text-sm text-slate-400">No messages with {whoPerson?.name} yet.</p>
                )}
                <div ref={endRef} />
              </div>
              <div className="relative border-t border-slate-100 p-2.5">
                {/* CHAT-03: tag a customer or job — the chip opens the record. */}
                {tagOpen && (
                  <TagPicker
                    onClose={() => { setTagOpen(false); setFromAt(false); }}
                    onPick={(kind, id, label) => {
                      if (fromAt) setDraft((d) => d.replace(/@$/, ""));
                      setPendingTags((ts) => [...ts, { kind, id, label }]);
                      setTagOpen(false); setFromAt(false);
                    }}
                  />
                )}
                {/* Picked tags ride as clean chips — X pulls one back off. */}
                {pendingTags.length > 0 && (
                  <div className="mb-1.5 flex flex-wrap items-center gap-1.5">
                    {pendingTags.map((t, i) => {
                      const Icon = TAG_ICON[t.kind] ?? User;
                      return (
                        <span key={`${t.kind}-${t.id}-${i}`} className="inline-flex items-center gap-1 rounded-full bg-brand-50 py-0.5 pl-2 pr-1 text-[12px] font-bold text-brand-700 ring-1 ring-brand-200">
                          <Icon className="h-3 w-3" />
                          {t.label}
                          <button type="button" onClick={() => setPendingTags((ts) => ts.filter((_, j) => j !== i))}
                            className="rounded-full p-0.5 text-brand-400 hover:bg-brand-100 hover:text-brand-700" aria-label={`Remove ${t.label}`}>
                            <X className="h-3 w-3" />
                          </button>
                        </span>
                      );
                    })}
                    <span className="text-[10px] font-medium text-slate-400">rides with your message — the chip opens the record</span>
                  </div>
                )}
                {/* CHAT-02: the emoji library — tap to drop one into the draft. */}
                {emojiOpen && !tagOpen && (
                  <div className="absolute bottom-full left-2 right-2 z-10 mb-1 overflow-hidden rounded-xl border border-hairline bg-white shadow-xl">
                    <div className="flex gap-1 overflow-x-auto border-b border-slate-100 px-2 py-1.5">
                      {EMOJI_GROUPS.map((g, i) => (
                        <button key={g.label} type="button" onClick={() => setEmojiTab(i)}
                          className={cn("shrink-0 rounded-full px-2.5 py-1 text-[11px] font-bold transition-colors",
                            emojiTab === i ? "bg-brand-700 text-white" : "text-slate-500 hover:bg-slate-100")}>
                          {g.label}
                        </button>
                      ))}
                    </div>
                    <div className="grid max-h-40 grid-cols-8 gap-0.5 overflow-y-auto p-2">
                      {EMOJI_GROUPS[emojiTab].emoji.map((e) => (
                        <button key={e} type="button" onClick={() => setDraft((d) => d + e)}
                          className="flex h-9 w-9 items-center justify-center rounded-lg text-xl transition-colors hover:bg-slate-100">
                          {e}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                <div className="flex items-end gap-1">
                  <button type="button" onClick={() => { setEmojiOpen((v) => !v); setTagOpen(false); }}
                    title="Emoji"
                    className={cn("flex h-10 w-9 shrink-0 items-center justify-center rounded-xl transition-colors",
                      emojiOpen ? "bg-brand-50 text-brand-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600")}>
                    <Smile className="h-5 w-5" />
                  </button>
                  <button type="button" onClick={() => { setTagOpen((v) => !v); setEmojiOpen(false); setFromAt(false); }}
                    title="Tag a customer or job — the chip opens the record (or just type @)"
                    className={cn("flex h-10 w-9 shrink-0 items-center justify-center rounded-xl transition-colors",
                      tagOpen ? "bg-brand-50 text-brand-700" : "text-slate-400 hover:bg-slate-100 hover:text-slate-600")}>
                    <AtSign className="h-5 w-5" />
                  </button>
                  <button type="button" onClick={() => fileRef.current?.click()} disabled={uploading}
                    title="Attach a file or photo (up to 25 MB)"
                    className="flex h-10 w-9 shrink-0 items-center justify-center rounded-xl text-slate-400 transition-colors hover:bg-slate-100 hover:text-slate-600 disabled:opacity-50">
                    {uploading ? <Loader2 className="h-5 w-5 animate-spin" /> : <Paperclip className="h-5 w-5" />}
                  </button>
                  <input ref={fileRef} type="file" className="hidden" onChange={(e) => void attachFile(e.target.files)} />
                  <textarea
                    value={draft}
                    onChange={(e) => {
                      const v = e.target.value;
                      setDraft(v);
                      // Grow with the text (wrapped lines included) up to ~6 lines
                      // — nothing gets cut off while typing (Seth 2026-08-31).
                      const el = e.currentTarget;
                      el.style.height = "auto";
                      el.style.height = `${Math.min(el.scrollHeight, 152)}px`;
                      // Typing "@" (at start or after a space) opens the tag picker.
                      if (/(^|\s)@$/.test(v)) { setTagOpen(true); setEmojiOpen(false); setFromAt(true); }
                    }}
                    onKeyDown={(e) => { if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); doSend(); e.currentTarget.style.height = "auto"; } }}
                    placeholder={`Message ${whoPerson?.name}…`}
                    rows={1}
                    className="max-h-40 min-h-[44px] min-w-0 flex-1 resize-none rounded-xl border border-hairline px-3 py-2.5 text-[14px] leading-relaxed focus:border-brand-400 focus:outline-none"
                  />
                  <button type="button" disabled={(!draft.trim() && pendingTags.length === 0) || send.isPending} onClick={doSend}
                    title="Send (Enter)"
                    className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-brand-700 text-white transition-colors hover:bg-brand-800 disabled:bg-brand-300">
                    <Send className="h-4 w-4" />
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      )}
    </>
  );
}
